import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { withDebounce } from '@frontend/jetlend-core/src/async/debounce';
import { DadataType } from '@frontend/jetlend-core/src/models/dadata';
import { connectToField } from '@frontend/jetlend-core/src/ui/inputs/connect';
import { UnexpectedError } from '@frontend/jetlend-core/src/ducks/utils';
import { buildClassNames } from '../../../utils/classNameUtils';
import {
    InlineTextInputComponent,
    IProps as InlineTextInputProps,
} from '../InlineTextInput/InlineTextInput';

import styles from './dadataInlineTextInput.module.scss';
import ClientPortal from '../../ClientPortal/ClientPortal';
import { isIOSDevice } from '@ui/utils/browserUtils';

const MIN_DROPDOWN_HEIGHT = 150;

// TODO Hide menu when input container hides from the screen
// TODO Render loaders on input component
// TODO Implement smooth animations for opening / closing
// TODO Implement keyboard navigation (arrow up/down for choosing; esc - close; tab - select;)

export interface IProps extends InlineTextInputProps {
    dadataToken: string;
    dadataType: DadataType;
    renderItem?: (response: any) => JSX.Element|React.ReactNode;
    formatSelected?: (response: any) => any;
    onSuggestionSelect?: (response: any) => void;
}

function toPx(input: number): string {
    return `${input.toFixed(0)}px`;
}

type DropdownItemWrapperProps = {
    response: any;
    renderItem?: (response: any) => JSX.Element|React.ReactNode;
    onClick?: (response: any) => void;
}

export const renderBankItem = suggestion => (
    <div className={styles['suggestion-item']}>
        <div className={styles['suggestion-item__title']}>
            {suggestion.data?.name?.payment}
        </div>
        <div className={styles['suggestion-item__details']}>
            <span className={styles['suggestion-item__detail']}>
                {suggestion.data?.bic}
            </span>
            <span className={styles['suggestion-item__detail']}>
                {suggestion.data?.address?.value}
            </span>
        </div>
    </div>
);

export const renderCompanyItem = suggestion => (
    <div className={styles['suggestion-item']}>
        <div className={styles['suggestion-item__title']}>
            {suggestion.value}
        </div>
        <div className={styles['suggestion-item__details']}>
            <span className={styles['suggestion-item__detail']}>
                {suggestion.data?.inn}
            </span>
            <span className={styles['suggestion-item__detail']}>
                {suggestion.data?.address?.value}
            </span>
        </div>
        {suggestion.data?.type !== 'INDIVIDUAL' && (
            <div className={styles['suggestion-item__details']}>
                <span className={styles['suggestion-item__detail']}>
                    Директор: {suggestion.data?.management?.name}
                </span>
            </div>
        )}
    </div>
);

export const renderFmsUnitItem = suggestion => (
    <div className={styles['suggestion-item']}>
        <div className={styles['suggestion-item__title']}>
            {suggestion.data?.name}
        </div>
        <div className={styles['suggestion-item__details']}>
            <span className={styles['suggestion-item__detail']}>
                {suggestion.data?.code}
            </span>
        </div>
    </div>
);

const DropdownItemWrapper: React.FC<DropdownItemWrapperProps> = props => {
    const content = useMemo(() => {
        if (props.renderItem) {
            return props.renderItem(props.response);
        }

        return <div className={styles['dropdown-item__text']}>{props.response?.value}</div>;
    }, [ props.renderItem, props.response ]);

    const didClicked = useCallback(() => {
        props.onClick && props.onClick(props.response);
    }, [ props.response, props.onClick ]);

    return (
        <button className={styles['dropdown-item__button']} onClick={didClicked}>
            {content}
        </button>
    );
};

async function requestDadataAsync(dadataType: DadataType, dadataToken: string, query: string): Promise<any> {
    const response = await fetch(`https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/${dadataType.toLowerCase()}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            Authorization: `Token ${dadataToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            count: 10,
            query,
        }),
    });

    return response.json();
}

export const DadataInlineTextInputComponent: React.FC<IProps> = props => {
    const {
        dadataToken,
        dadataType,
        renderItem,
        formatSelected,
        onSuggestionSelect,
        onFocus,
        onBlur,
        ...rest
    } = props;

    const containerRef = useRef<HTMLDivElement>();
    const dropdownRef = useRef<HTMLDivElement>();

    const isIOS = isIOSDevice();

    const [ shown, setShow ] = useState(false);
    const [ dropDown, setDropDown ] = useState(true);
    const [ searching, setSearching ] = useState(false);

    const [ suggestions, setSuggestions ] = useState<any[]>([]);

    const didSearch = useMemo(() => withDebounce(async (searchValue: string) => {
        try {
            if (searchValue?.length > 0) {
                setSearching(true);

                const response = await requestDadataAsync(dadataType, dadataToken, searchValue);

                setSearching(false);
                if (response && response.suggestions && Array.isArray(response.suggestions)) {
                    setSuggestions([ ...response.suggestions ]);
                } else {
                    setSuggestions([]);
                }
            }
        } catch (e) {
            setSearching(false);
            console.error('DADATA ERROR', e);
        }
    }, { timeout: 500 }), [ dadataToken, dadataType ]);

    useEffect(() => {
        if (shown && typeof props.value === 'string' && props.value?.length > 0) {
            didSearch(props.value);
        }
    }, [ shown, props.value, didSearch ]);

    const didFocused = useCallback(e => {
        setShow(true);

        if (typeof props.value === 'string' && props.value?.length > 0) {
            didSearch(props.value);
        }

        onFocus && onFocus(e);
    }, [ onFocus, props.value, didSearch ]);

    const didBlur = useCallback(e => {
        const timeoutId = setTimeout(() => setShow(false), 350);

        onBlur && onBlur(e);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [ onBlur ]);

    const didItemSelected = useCallback((response: any) => {
        setShow(false);

        const selectedTextValue = formatSelected
            ? formatSelected(response)
            : response?.value;

        props.onChange && props.onChange(selectedTextValue);

        onSuggestionSelect && onSuggestionSelect(response);

        if (!response) {
            console.error(new UnexpectedError('DadataInlineTextInput error: selected response is null or undefined'));
        }
    }, [ props.onChange, formatSelected, onSuggestionSelect ]);

    useEffect(() => {
        if (!shown) {
            return;
        }

        let animate = true;

        function tick() {
            const container = containerRef.current;
            const dropdown = dropdownRef.current;
            if (!container || !dropdown) {
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const dropdownRect = dropdown.getBoundingClientRect();
            const dropdownHeight = dropdownRect.height;
            const windowHeight = window.innerHeight;

            const x = containerRect.x;
            const bottomY = isIOS
                ? containerRect.y + containerRect.height + window.scrollY
                : containerRect.y + containerRect.height;
            const availableBottomHeight = windowHeight - bottomY - 10;

            if (availableBottomHeight >= MIN_DROPDOWN_HEIGHT) {
                // Drop to the down
                dropdown.style.transform = `translate(${toPx(x)}, ${toPx(bottomY)})`;
                dropdown.style.maxHeight = toPx(availableBottomHeight);
                setDropDown(true);
            } else {
                // Drop to the up
                const availableTopHeight = containerRect.y - 10;
                const topY = containerRect.y - Math.min(dropdownHeight, availableTopHeight);

                dropdown.style.transform = `translate(${toPx(x)}, ${toPx(topY)})`;
                dropdown.style.maxHeight = toPx(availableTopHeight);
                setDropDown(false);
            }

            dropdown.style.width = toPx(containerRect.width);

            if (animate) {
                requestAnimationFrame(tick);
            }
        }

        requestAnimationFrame(tick);
        tick();

        return () => {
            animate = false;
        };
    }, [ shown, containerRef, dropdownRef ]);

    const dropdownContainerClassName = buildClassNames(styles, [
        'dropdown-container',
        shown && 'dropdown-container--active',
        dropDown ? 'dropdown-container--drop-down' : 'dropdown-container--drop-up',
    ]);

    return (
        <>
            <InlineTextInputComponent
                {...rest}
                ref={containerRef as any}
                autoComplete={false}
                onFocus={didFocused}
                onBlur={didBlur}
            />

            <ClientPortal>
                <div ref={dropdownRef} className={dropdownContainerClassName}>
                    {/* {searching &&
                    <div className={styles['dropdown-item__loader']}>
                        <LoaderBlock size="normal" />
                    </div>
                } */}
                    {suggestions?.length === 0 && (
                        <div className={styles['dropdown-item__empty']}>
                            Ничего не найдено
                        </div>
                    )}
                    {suggestions?.length > 0 && suggestions.map((item, index) =>
                        <DropdownItemWrapper key={`$$index__${index}`} response={item} onClick={didItemSelected} renderItem={renderItem} />
                    )}
                </div>
            </ClientPortal>
        </>
    );
};

const DadataInlineTextInput = connectToField(DadataInlineTextInputComponent);

export default DadataInlineTextInput;